/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';

import { getMsalInstance } from '@/lib/msal';
import { Spinner } from '@/components/spinner';

type EntraAuthGuardProps = Readonly<{
  children: React.ReactNode;
  isEntraId: boolean;
}>;

/**
 * entra_id has no server-rendered session (see withAuthMiddleware), so
 * directly navigating to a protected route would otherwise render it before
 * anyone checked whether the browser actually holds a signed-in MSAL account.
 * isEntraId must come from the server (not the client-injected runtime
 * config), otherwise SSR renders the real page - the config global doesn't
 * exist yet during that pass - and the protected content flashes before the
 * client-side check ever runs.
 */
export const EntraAuthGuard = ({
  children,
  isEntraId,
}: EntraAuthGuardProps) => {
  const router = useRouter();
  const params = useParams<{ locale: string }>();
  const [isChecking, setIsChecking] = useState(isEntraId);

  useEffect(() => {
    if (!isEntraId) return;

    let cancelled = false;

    const verifySession = async () => {
      try {
        const instance = await getMsalInstance();
        const account =
          instance.getActiveAccount() ?? instance.getAllAccounts()[0];

        if (!account) {
          router.replace(`/${params.locale}`);
          return;
        }
      } catch {
        router.replace(`/${params.locale}`);
        return;
      }

      if (!cancelled) setIsChecking(false);
    };

    void verifySession();

    return () => {
      cancelled = true;
    };
  }, [isEntraId, params.locale, router]);

  if (isChecking) {
    return <Spinner label="Checking your session..." />;
  }

  return <>{children}</>;
};
